(function () {
  const drawer = document.querySelector('[data-vault-cart-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('.vault-cart-drawer__panel');
  const body = drawer.querySelector('[data-vault-cart-body]');
  const footer = drawer.querySelector('[data-vault-cart-footer]');
  const subtotal = drawer.querySelector('[data-vault-cart-subtotal]');
  const status = drawer.querySelector('[data-vault-cart-status]');
  const labels = drawer.dataset;
  const moneyFormatter = new Intl.NumberFormat(labels.locale || document.documentElement.lang || 'en', {
    style: 'currency',
    currency: labels.currency || 'EUR'
  });

  let activeCart = null;
  let isBusy = false;

  function endpoint(url) {
    return url || '/cart.js';
  }

  function formatMoney(cents) {
    return moneyFormatter.format((Number(cents) || 0) / 100);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeImage(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  function setStatus(message, isError) {
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  function setBusy(busy) {
    isBusy = busy;
    drawer.classList.toggle('is-loading', busy);
  }

  function updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((cartCount) => {
      cartCount.textContent = cart.item_count;
      cartCount.classList.toggle('is-empty', cart.item_count === 0);
    });

    document.querySelectorAll('a[href*="/cart"]').forEach((cartLink) => {
      cartLink.setAttribute(
        'aria-label',
        `${labels.labelTitle || 'Cart'}, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`
      );
    });
  }

  function hasPreorderSignal(item) {
    const text = [
      item.product_title,
      item.title,
      item.variant_title,
      Object.values(item.properties || {}).join(' ')
    ]
      .join(' ')
      .toLowerCase();

    return text.includes('pre-order') || text.includes('preorder') || text.includes('prednarudzba');
  }

  function renderEmpty() {
    if (!body) return;
    body.innerHTML = `
      <div class="vault-cart-drawer__empty">
        <p>${escapeHtml(labels.labelEmpty || 'Your vault is empty.')}</p>
        <a class="button" href="${escapeHtml(labels.productsUrl || '/pages/products')}">
          ${escapeHtml(labels.labelBrowse || 'Browse vaults')}
        </a>
      </div>
    `;
    if (footer) footer.hidden = true;
  }

  function renderItem(item, index) {
    const image = normalizeImage(item.image);
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="vault-cart-drawer__variant">${escapeHtml(item.variant_title)}</p>`
      : '';
    const properties = Object.entries(item.properties || {})
      .filter(([key, value]) => value && !String(key).startsWith('_'))
      .map(([key, value]) => `${escapeHtml(key)}: ${escapeHtml(value)}`)
      .join('<br>');

    return `
      <article class="vault-cart-drawer__item" data-vault-cart-line="${index + 1}" data-vault-cart-key="${escapeHtml(item.key)}">
        <a class="vault-cart-drawer__media" href="${escapeHtml(item.url)}">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">` : ''}
        </a>
        <div class="vault-cart-drawer__item-content">
          <a class="vault-cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant}
          ${properties ? `<p class="vault-cart-drawer__properties">${properties}</p>` : ''}
          <span class="vault-cart-drawer__price">${formatMoney(item.final_line_price)}</span>
          <div class="vault-cart-drawer__controls">
            <div class="vault-cart-drawer__quantity" aria-label="${escapeHtml(labels.labelQuantity || 'Qty')}">
              <button type="button" data-vault-cart-quantity="-1" aria-label="Decrease quantity">-</button>
              <span>${item.quantity}</span>
              <button type="button" data-vault-cart-quantity="1" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="vault-cart-drawer__remove" data-vault-cart-remove>
              ${escapeHtml(labels.labelRemove || 'Remove')}
            </button>
          </div>
        </div>
      </article>
    `;
  }

  function renderCart(cart) {
    activeCart = cart;
    updateCartCount(cart);

    if (!body || !subtotal) return;

    if (!cart.items || cart.items.length === 0) {
      renderEmpty();
      return;
    }

    const hasPreorder = cart.items.some(hasPreorderSignal);
    body.innerHTML = `
      <div class="vault-cart-drawer__items">
        ${cart.items.map(renderItem).join('')}
      </div>
      ${hasPreorder ? `
        <div class="vault-cart-drawer__preorder-note" role="note">
          <strong>${escapeHtml(labels.labelPreorder || 'Pre-order')}</strong>
          ${escapeHtml(labels.labelPreorderNotice || '')}
        </div>
      ` : ''}
    `;

    subtotal.textContent = formatMoney(cart.total_price);
    if (footer) footer.hidden = false;
  }

  async function fetchCart() {
    const response = await fetch(endpoint(labels.cartJsonUrl), {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(labels.labelError || 'The cart could not be loaded.');
    return response.json();
  }

  async function refreshCart(options) {
    setBusy(true);
    try {
      const cart = await fetchCart();
      renderCart(cart);
      if (options && options.open) openDrawer();
      return cart;
    } catch (error) {
      setStatus(error.message || labels.labelError, true);
      throw error;
    } finally {
      setBusy(false);
    }
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    document.body.classList.add('vault-cart-drawer-open');
    window.setTimeout(() => panel && panel.focus(), 20);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.body.classList.remove('vault-cart-drawer-open');
  }

  async function addFormToCart(form) {
    if (isBusy) return;
    const submit = form.querySelector('[type="submit"]');
    const originalContent = submit ? submit.innerHTML : '';

    if (submit) {
      submit.disabled = true;
      submit.textContent = labels.labelLoading || 'Updating vault...';
    }

    setStatus('', false);
    setBusy(true);

    try {
      const response = await fetch(endpoint(labels.cartAddUrl), {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || labels.labelError || 'The item could not be added.');
      }

      setStatus(labels.labelAdded || 'Added to your vault.', false);
      await refreshCart({ open: true });
    } catch (error) {
      setStatus(error.message || labels.labelError, true);
      openDrawer();
    } finally {
      setBusy(false);
      if (submit) {
        submit.disabled = false;
        submit.innerHTML = originalContent;
      }
    }
  }

  async function changeLine(line, quantity) {
    if (isBusy || !line) return;
    setStatus('', false);
    setBusy(true);

    try {
      const response = await fetch(endpoint(labels.cartChangeUrl), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || labels.labelError || 'The cart could not be updated.');
      }

      renderCart(await response.json());
    } catch (error) {
      setStatus(error.message || labels.labelError, true);
    } finally {
      setBusy(false);
    }
  }

  drawer.addEventListener('click', (event) => {
    if (event.target.closest('[data-vault-cart-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const remove = event.target.closest('[data-vault-cart-remove]');
    if (remove) {
      const item = remove.closest('[data-vault-cart-line]');
      event.preventDefault();
      changeLine(Number(item?.dataset.vaultCartLine), 0);
      return;
    }

    const quantityButton = event.target.closest('[data-vault-cart-quantity]');
    if (quantityButton) {
      const item = quantityButton.closest('[data-vault-cart-line]');
      const line = Number(item?.dataset.vaultCartLine);
      const current = activeCart?.items?.[line - 1]?.quantity || 1;
      const next = Math.max(0, current + Number(quantityButton.dataset.vaultCartQuantity || 0));
      event.preventDefault();
      changeLine(line, next);
    }
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const url = new URL(link.href, window.location.origin);
    const cartUrl = new URL(labels.cartUrl || '/cart', window.location.origin);
    const isCartLink = url.origin === window.location.origin && url.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '');

    if (!isCartLink || link.hasAttribute('data-vault-cart-ignore')) return;

    event.preventDefault();
    refreshCart({ open: true }).catch(() => openDrawer());
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.closest('.main-product-section')) return;

    event.preventDefault();
    addFormToCart(form);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  document.addEventListener('vault:cart:updated', (event) => {
    if (event.detail && event.detail.cart) {
      renderCart(event.detail.cart);
      if (event.detail.open) openDrawer();
      return;
    }

    refreshCart({ open: Boolean(event.detail && event.detail.open) }).catch(() => {});
  });

  window.NimasVaultCartDrawer = {
    open: () => refreshCart({ open: true }).catch(() => openDrawer()),
    close: closeDrawer,
    refresh: refreshCart
  };
})();
